import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/tokens.css';
import './index.css';
import './styles/dark-shell.css';
import './styles/dark-bootstrap.css';
import './styles/dark-datatables.css';
import './styles/dark-notifications.css';
import './styles/dark-pages.css';
import './styles/tables.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ─── Suppress "ResizeObserver loop" dev-overlay error ────────────────────────
// React's error overlay registers its listener in the bubble phase. We register
// in the CAPTURE phase (3rd arg = true) so we run FIRST and can stop propagation
// before the overlay ever sees it. Also clears the webpack overlay iframe/div.
if (typeof window !== 'undefined') {
  const _suppressResizeObserverError = (e) => {
    if (
      e.message &&
      (e.message.includes('ResizeObserver loop completed with undelivered notifications') ||
        e.message.includes('ResizeObserver loop limit exceeded'))
    ) {
      e.stopImmediatePropagation();

      // Also hide the webpack-dev-server overlay iframe if it rendered anyway
      const overlay = document.getElementById('webpack-dev-server-client-overlay');
      const overlayDiv = document.getElementById('webpack-dev-server-client-overlay-div');
      if (overlay) overlay.style.display = 'none';
      if (overlayDiv) overlayDiv.style.display = 'none';
    }
  };

  // capture: true → runs before React's bubble-phase handler
  window.addEventListener('error', _suppressResizeObserverError, true);

  // Also override window.onerror as a second safety net
  const _origOnerror = window.onerror;
  window.onerror = function (msg, src, line, col, err) {
    if (
      typeof msg === 'string' &&
      (msg.includes('ResizeObserver loop completed with undelivered notifications') ||
        msg.includes('ResizeObserver loop limit exceeded'))
    ) {
      return true; // returning true prevents default error handling
    }
    return _origOnerror ? _origOnerror(msg, src, line, col, err) : false;
  };
}
// ─────────────────────────────────────────────────────────────────────────────


// Global DataTable Configuration
const configureDataTable = () => {
  if (typeof window === "undefined" || !window.DataTable) return;

  // 1. Set global defaults
  if (window.DataTable.defaults) {
    window.DataTable.defaults.scrollX = true;
  } else if (window.jQuery && window.jQuery.fn && window.jQuery.fn.dataTable && window.jQuery.fn.dataTable.defaults) {
    window.jQuery.fn.dataTable.defaults.scrollX = true;
  }

  // Prevent multiple bindings
  if (window.__dataTableConfigured) return;
  window.__dataTableConfigured = true;

  // 2. Globally listen for DataTable init events to inject Export CSV button
  const handleInit = (e) => {
    try {
      const tableElement = e.target;
      if (!tableElement) return;

      const instance = window.DataTable.Api 
        ? new window.DataTable.Api(tableElement) 
        : new window.DataTable(tableElement);

      const wrapper = tableElement.closest(".dt-container") || 
                      tableElement.closest(".dataTables_wrapper") || 
                      tableElement.parentNode;
      if (!wrapper) return;

      if (wrapper.querySelector(".btn-export-csv")) return;

      const target = wrapper.querySelector(".dt-search") || 
                     wrapper.querySelector(".dataTables_filter") || 
                     wrapper.querySelector(".dt-length") || 
                     wrapper.querySelector(".dataTables_length");

      const btn = document.createElement("button");
      btn.className = "btn btn-outline-primary btn-sm btn-export-csv ms-2 me-2 shadow-sm d-inline-flex align-items-center gap-1";
      btn.type = "button";
      btn.innerHTML = '<i class="fa fa-download"></i> Export CSV';
      btn.style.verticalAlign = "middle";

      btn.onclick = () => {
        const csv = [];
        let rows = [];
        
        // Support split tables (scrollX splits table into header and body tables)
        const headerTable = wrapper ? (wrapper.querySelector(".dt-scroll-head table, .dataTables_scrollHead table") || tableElement) : tableElement;
        const bodyTable = wrapper ? (wrapper.querySelector(".dt-scroll-body table, .dataTables_scrollBody table") || tableElement) : tableElement;
        
        // Use bodyTable instance if available
        const activeInstance = bodyTable._dt || instance;

        if (activeInstance && typeof activeInstance.rows === "function") {
          try {
            const dtRows = activeInstance.rows({ search: "applied" }).nodes();
            if (dtRows && typeof dtRows.toArray === "function") {
              rows = dtRows.toArray();
            } else if (dtRows && dtRows.length) {
              rows = Array.from(dtRows);
            }
          } catch (err) {
            console.warn("Failed to get rows via DataTable API, falling back to DOM:", err);
          }
        }

        if (!rows || rows.length === 0) {
          rows = Array.from(bodyTable.querySelectorAll("tbody tr"));
        }

        const headerRows = headerTable.querySelectorAll("thead tr");
        const headerRow = headerRows.length > 0 ? headerRows[headerRows.length - 1] : headerTable.querySelector("tr");
        const skipColumnIndices = new Set();
        const taxColumnIndices = new Set();
        if (headerRow) {
          const headers = headerRow.querySelectorAll("th, td");
          const headerData = [];
          headers.forEach((th, index) => {
            const text = (th.innerText || "").trim();
            const lowerText = text.toLowerCase();
            if (
              lowerText === "action" || 
              lowerText === "actions" || 
              lowerText === "edit" || 
              lowerText === "delete" || 
              lowerText === "manage" ||
              lowerText === "select" ||
              lowerText === "checkbox"
            ) {
              skipColumnIndices.add(index);
              return;
            }

            if (lowerText.startsWith("tax ") || lowerText === "tax") {
              taxColumnIndices.add(index);
              headerData.push(`"${text} Name"`);
              headerData.push(`"${text} Value"`);
            } else {
              let cleanedText = text.replace(/(\r\n|\n|\r)/gm, " ").replace(/\s+/g, " ");
              cleanedText = cleanedText.replace(/"/g, '""');
              headerData.push(`"${cleanedText}"`);
            }
          });

          if (headerData.length > 0) {
            csv.push(headerData.join(","));
          }
        }

        for (let i = 0; i < rows.length; i++) {
          const row = [];
          const cols = rows[i].querySelectorAll("td, th");

          for (let j = 0; j < cols.length; j++) {
            if (skipColumnIndices.has(j)) continue;

            const col = cols[j];
            
            if (taxColumnIndices.has(j)) {
              let nameText = "";
              let valueText = "";
              
              const childDivs = Array.from(col.children).filter(el => el.tagName === "DIV");
              if (childDivs.length >= 2) {
                nameText = (childDivs[0].innerText || "").replace(/(\r\n|\n|\r)/gm, " ").replace(/\s+/g, " ").trim();
                valueText = (childDivs[1].innerText || "").replace(/(\r\n|\n|\r)/gm, " ").replace(/\s+/g, " ").trim();
              } else {
                const text = (col.innerText || "").trim();
                const parts = text.split("\n");
                if (parts.length >= 2) {
                  nameText = parts[0].trim();
                  valueText = parts[1].trim();
                } else if (text === "-") {
                  nameText = "-";
                  valueText = "-";
                } else {
                  nameText = text;
                  valueText = "";
                }
              }
              
              nameText = nameText.replace(/"/g, '""');
              valueText = valueText.replace(/"/g, '""');
              row.push(`"${nameText}"`);
              row.push(`"${valueText}"`);
            } else {
              let text = "";

              const input = col.querySelector("input[type='text'], input[type='number'], select");
              if (input) {
                text = input.value || "";
              } else {
                text = col.innerText || "";
              }

              text = text.replace(/(\r\n|\n|\r)/gm, " ").replace(/\s+/g, " ").trim();
              text = text.replace(/"/g, '""');
              row.push(`"${text}"`);
            }
          }

          if (row.length > 0) {
            csv.push(row.join(","));
          }
        }

        if (csv.length === 0) return;

        const csvString = "\uFEFF" + csv.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        let filename = "export.csv";
        try {
          const titleEl = document.querySelector(".page-header h5") || 
                          document.querySelector(".page-header h3") || 
                          document.querySelector("h4") || 
                          document.querySelector(".card-title");
          if (titleEl && titleEl.innerText) {
            filename = `${titleEl.innerText.trim().toLowerCase().replace(/\s+/g, "_")}_export.csv`;
          } else if (tableElement.id) {
            filename = `${tableElement.id}_export.csv`;
          }
        } catch (err) {}

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      if (target) {
        target.appendChild(btn);
      } else {
        tableElement.parentNode.insertBefore(btn, tableElement);
      }
    } catch (err) {
      console.error("DataTable custom wrapping injection error:", err);
    }
  };

  document.addEventListener("init.dt", handleInit);
};

if (typeof window !== "undefined") {
  if (window.DataTable) {
    configureDataTable();
  } else {
    window.addEventListener("load", configureDataTable);
    const interval = setInterval(() => {
      if (window.DataTable) {
        configureDataTable();
        clearInterval(interval);
      }
    }, 100);
    setTimeout(() => clearInterval(interval), 5000);
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function/
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
